import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useWorkspace } from '../../../../contexts/WorkspaceContext';
import { useCreateModalStore, type CreateRequest } from '../../../../stores/createModalStore';
import { useDefaultCreateDestination } from '../../../../hooks/create/useDefaultCreateDestination';
import {
  purposeCollectionForCreateType,
  usePurposeCollections,
} from '../../../../hooks/data/usePurposeCollections';
import { safeAnalytics } from '../../../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../../../constants/analytics';
import {
  collectionDestination,
  destinationProblemMessage,
  type CreateDestination,
} from '../destination/createDestination';

export type DestinationSource =
  | 'context'
  | 'entry_point'
  | 'picker'
  | 'new_collection'
  /** A suggested collection (decision 0018): no collection in view, a protocol or equipment. */
  | 'purpose';

/** Where the modal is: choosing a type, a type's own step, or the picker. */
export type CreateModalView =
  | { readonly kind: 'choose' }
  | { readonly kind: 'details'; readonly type: string }
  | {
      readonly kind: 'picker';
      readonly returnTo: Exclude<CreateModalView, { kind: 'picker' }>;
      readonly message?: string;
      /** Its inline New collection form is open: Back returns to the list. */
      readonly creating?: boolean;
    };

/** The type whose step is open, directly or under the picker. */
function detailsTypeOf(view: CreateModalView): string | null {
  if (view.kind === 'details') return view.type;
  if (view.kind === 'picker' && view.returnTo.kind === 'details') return view.returnTo.type;
  return null;
}

/** What one open request settled on: taken once, so nothing behind the modal moves it. */
interface Snapshot {
  readonly request: CreateRequest;
  readonly destination: CreateDestination;
  readonly source: DestinationSource;
  readonly fromView: boolean;
  /** The collection in view could not be the default: the user can only view it. */
  readonly viewOnlyName?: string;
}

/**
 * The modal's state for one open request: the destination (snapshotted once
 * the default is known, so navigating behind the modal moves nothing; it is
 * also where a new collection goes), where it came from, the current view and
 * whether a body has a request in flight. Closes itself on a workspace switch
 * (a scope id from the old workspace must never be sent under the new one) and
 * on any route change it did not cause. A request with no workspace to create
 * in is dropped rather than left open and invisible.
 */
export function useCreateModalController() {
  const request = useCreateModalStore((s) => s.request);
  const close = useCreateModalStore((s) => s.close);
  const { workspace } = useWorkspace();
  const fallback = useDefaultCreateDestination(!!request);
  const purposes = usePurposeCollections(!!request);
  const location = useLocation();

  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [view, setView] = useState<CreateModalView>({ kind: 'choose' });
  const [busy, setBusy] = useState(false);
  const openedIn = useRef<{
    request: CreateRequest;
    workspaceId?: string;
    path: string;
  } | null>(null);
  const purposesLoading = purposes.isLoading;
  const latest = useRef({
    fallback,
    purposesLoading,
    workspaceId: workspace?.id,
    path: location.pathname,
  });
  latest.current = {
    fallback,
    purposesLoading,
    workspaceId: workspace?.id,
    path: location.pathname,
  };

  useEffect(() => {
    if (!request) {
      setSnap(null);
      setView({ kind: 'choose' });
      setBusy(false);
      openedIn.current = null;
      return;
    }
    if (snap?.request === request) return;
    const { fallback: start, workspaceId, path } = latest.current;
    // Where it was asked for, from the first render: a route change while the
    // default is still loading closes it too.
    if (openedIn.current?.request !== request) openedIn.current = { request, workspaceId, path };
    // Wait for the default, and for the suggested collections a protocol or
    // equipment would file into (a failed read just falls back to the root).
    if (!request.destination && (!start.ready || latest.current.purposesLoading)) return;
    const destination = request.destination ?? start.destination;
    if (!destination) {
      close();
      return;
    }
    setSnap({
      request,
      destination,
      source: request.destination ? 'entry_point' : 'context',
      fromView: !request.destination && start.fromView,
      viewOnlyName: request.destination ? undefined : start.viewOnlyName,
    });
    setView(request.type ? { kind: 'details', type: request.type } : { kind: 'choose' });
    setBusy(false);
    safeAnalytics.track(ANALYTICS_EVENTS.CREATE_MODAL_OPENED, {
      surface: request.surface,
      preselected_type: request.type ?? null,
      destination_type: destination.type,
    });
  }, [request, snap, fallback.ready, purposesLoading, close]);

  useEffect(() => {
    const opened = openedIn.current;
    if (!opened) return;
    // Asked for before any workspace had loaded (page boot): the first one to
    // arrive is where it was asked, not a switch away from an old one.
    if (opened.workspaceId === undefined) opened.workspaceId = workspace?.id;
    if (workspace?.id !== opened.workspaceId || location.pathname !== opened.path) close();
  }, [workspace?.id, location.pathname, close]);

  const isOpen = !!request && snap?.request === request;
  // Asked to open, still learning the default destination (the collection in
  // view can take a fetch): the modal shows at once and fills in when ready.
  const isLoading = !!request && !isOpen;

  const chooseDestination = useCallback((next: CreateDestination, how: DestinationSource) => {
    setSnap((current) =>
      current ? { ...current, destination: next, source: how, fromView: false } : current,
    );
  }, []);

  // With no collection in view, a protocol or equipment defaults to its
  // suggested collection while the type's step is open (or the picker over
  // it). Derived, not stored: Back to the type list returns to the root, and
  // any choice the user makes (picker, new collection) wins.
  const purposeTarget =
    isOpen && snap.source === 'context' && !snap.fromView && snap.destination.type === 'workspace'
      ? purposeCollectionForCreateType(purposes.data, detailsTypeOf(view))
      : null;
  let destination: CreateDestination | null = null;
  if (purposeTarget) destination = collectionDestination(purposeTarget);
  else if (isOpen) destination = snap.destination;
  const source: DestinationSource = purposeTarget ? 'purpose' : (snap?.source ?? 'context');
  const reportDestinationProblem = useCallback(
    (problem: 'gone' | 'forbidden') => {
      if (!destination) return;
      setBusy(false);
      setView((current) => ({
        kind: 'picker',
        returnTo: current.kind === 'picker' ? current.returnTo : current,
        message: destinationProblemMessage(problem, destination),
      }));
    },
    [destination],
  );

  return useMemo(
    () => ({
      request: isOpen ? request : null,
      /** The request still waiting on its default destination. */
      pendingRequest: isLoading ? request : null,
      isOpen,
      isLoading,
      destination,
      source,
      fromView: isOpen && snap.fromView,
      viewOnlyName: isOpen ? snap.viewOnlyName : undefined,
      view,
      setView,
      busy,
      setBusy,
      chooseDestination,
      reportDestinationProblem,
      close,
    }),
    [
      isOpen,
      isLoading,
      request,
      destination,
      source,
      snap,
      view,
      busy,
      chooseDestination,
      reportDestinationProblem,
      close,
    ],
  );
}

export type CreateModalController = ReturnType<typeof useCreateModalController>;
